import { createAction, props } from '@ngrx/store';
import {
  irbPriorityTypes,
  Submission,
  SubmissionDialogData,
  SubmissionQcStatus,
  SubmissionResponse,
  SubmissionTodoCounts,
  UpdateSubmissionData,
} from '../models/submission.model';
import { HttpParams } from '@angular/common/http';
import { ComponentType } from '@angular/cdk/overlay';
import { FailedDocument, UploadState } from '@data-access/submissions';
import { Sites } from '@submissions/v1/content/sites-section/types';
import { WcgMenuOption } from '@wcg/frontend-framework';
import { DocumentDetail } from '@data-access/documents';

export const noop = createAction('[Submission] Noop');

export const storeSubmission = createAction(
  '[Submission] Store Submission',
  props<{ submission: Submission }>(),
);

export const storeActiveSubmissionId = createAction(
  '[Submission] Store Active Submission Id',
  props<{ id: string }>(),
);
export const storeSubmissionList = createAction(
  '[Submission] Store Submission List',
  props<{ submissions: Submission[] }>(),
);

export const loadingSubmission = createAction(
  '[Submission] Loading Submission',
  props<{ id?: string }>(),
);

export const loadingSubmissionList = createAction(
  '[Submission] Loading Submission List',
);

export const storeWorkspaceTypes = createAction(
  '[Submission] Store Workspace Types',
  props<{ workspaceTypes: any[] }>(),
);

export const loadingWorkspaceTypes = createAction(
  '[Submission] Loading Workspace Types',
);

export const storeReviewMaterials = createAction(
  '[Review] Store Review Materials Documents',
  props<{ documents: any[] }>(),
);

export const updateStoreReviewMaterials = createAction(
  '[Review] Update Store Review Materials Documents',
  props<{ documents: unknown[] }>(),
);

export const storeMaterialDocumentTypes = createAction(
  '[Review] Store Material Document Types',
  props<{ documentTypes: any[] }>(),
);

export const storeLoadingReviewMaterials = createAction(
  '[Review] Loading Review Materials',
);

export const updateSubmissionStatus = createAction(
  '[Submission] Update Submission Status',
  props<{
    qcStatus: SubmissionQcStatus;
    todoCounts: SubmissionTodoCounts;
    hasNotificationsDetails: boolean;
    hasNotificationsDocuments: boolean;
  }>(),
);
export const setReviewMaterialDocuments = createAction(
  '[Review] Set Review Materials Documents',
  props<{ document: any }>(),
);

export const setReviewMaterialDocumentsSuccess = createAction(
  '[Review] Set Review Materials Documents Success',
  props<{ document: any }>(),
);

export const setReviewMaterialDocumentsFailure = createAction(
  '[Review] Set Review Materials Documents Failure',
  props<{ error: string }>(),
);

export const updatingReviewerDocumentList = createAction(
  '[Review] Updating Reviewer Document List',
  props<{ document: any }>(),
);

export const deleteReviewMaterialDocuments = createAction(
  '[Review] Delete Review Materials Documents',
  props<{ documentId: string }>(),
);

export const deleteReviewMaterialDocumentsSuccess = createAction(
  '[Review] Delete Review Materials Documents Success',
  props<{ documentId: string }>(),
);

export const deleteReviewMaterialDocumentsFailure = createAction(
  '[Review] Delete Review Materials Documents Failed',
  props<{ error: string }>(),
);

export const uploadingReviewerDocument = createAction(
  '[Document] Uploading Reviewer Document',
  props<{
    uuid: string;
    workspaceId: string;
    filename: string;
    reviewDocumentTypeId: string;
    reviewDocumentTypeName: string;
  }>(),
);

export const addSitesToSubmission = createAction(
  '[Submission] Add Site to Submission',
  props<{ submissionId: string; siteIds: string[]; reason?: string }>(),
);

export const addSitesToSubmissionSuccess = createAction(
  '[Submission] Add Site to Submission Success',
);

export const addSitesToSubmissionFailure = createAction(
  '[Submission] Add Site to Submission Failure',
  props<{ error: string }>(),
);

export const getSubmissionSites = createAction(
  '[Submission] Get Submission Sites',
  props<{
    submissionId: string;
    queryParams: HttpParams;
    ignoreHeaders?: boolean;
  }>(),
);

export const getSitesForStudy = createAction(
  '[Submission] Get Sites for Study',
  props<{ studyId: string; queryParams: HttpParams }>(),
);

export const getSitesForWorkspace = createAction(
  '[Submission] Get Sites for Workspace',
  props<{ submissionId: string; queryParams: HttpParams }>(),
);

export const getSubmissionSitesSuccess = createAction(
  '[Submission] Get Submission Sites Success',
  props<{
    sites: Sites[];
    statuses: WcgMenuOption[];
    ignoreHeaders: boolean;
    total: number;
  }>(),
);

export const getStudySitesSuccess = createAction(
  '[Submission] Get Study Sites Success',
  props<{ sites: any[]; statuses: any[]; total: number }>(),
);

export const getSubmissionSitesFailure = createAction(
  '[Submission] Get Submission Sites Failure',
  props<{ error: string }>(),
);

export const removeSitesFromSubmission = createAction(
  '[Submission] Remove Sites from Submission',
  props<{ submissionId: string; siteIds: string[]; reason: string }>(),
);

export const removeSitesFromSubmissionSuccess = createAction(
  '[Submission] Remove Sites from Submission Success',
);

export const removeSitesFromSubmissionFailure = createAction(
  '[Submission] Remove Sites from Submission Failure',
  props<{ error: string }>(),
);

export const irbPriorityChange = createAction(
  '[Submission] IRB Priority Change',
  props<{ workspaceId: string; priority: irbPriorityTypes }>(),
);

export const irbPriorityChangeSuccess = createAction(
  '[Submission] IRB Priority Change Success',
  props<{ response: SubmissionResponse }>(),
);

export const irbPriorityChangeFailure = createAction(
  '[Submission] IRB Priority Change Failure',
  props<{ error: string }>(),
);
export const irbPriorityView = {
  irbPriorityChangeSuccess,
  irbPriorityChangeFailure,
};

export const submissionHeaderDialog = createAction(
  '[SubmissionHeaderDialog] Submission Header Dialog',
  props<{
    component: () => ComponentType<unknown>;
    data: SubmissionDialogData;
  }>(),
);

export const onSubmssionHeaderDialoagClose = createAction(
  '[SubmissionHeaderDialog] Submission Header Dialog Close',
  props<{
    data: UpdateSubmissionData;
  }>(),
);

export const onDocumentUploadFailRefresh = createAction(
  '[Submission] Upload Document Banner Status',
  props<{
    message: string;
  }>(),
);

export const onDocumentUploadFailedDocuments = createAction(
  '[Submission] update failed documents',
  props<{
    failedDocuments: FailedDocument[];
  }>(),
);

export const acknowledgeFailedDocuments = createAction(
  '[Submission] Acknowledge Failed Documents',
  props<{ workspaceId: string; documentIds: string[] }>(),
);

export const onAcknowledgeFailedDocumentsSuccess = createAction(
  '[Submission] Acknowledge Failed Documents Success',
  props<{ closeBanner: boolean }>(),
);

export const onAcknowledgeFailedDocumentsFailure = createAction(
  '[Submission] Acknowledge Failed Documents Failure',
  props<{ error: Error }>(),
);

export const onDocumentUploadStatusChanged = createAction(
  '[Submission] Document Upload Status Changed',
  props<{
    document: DocumentDetail;
    status: UploadState;
    error: string | null;
  }>(),
);

export const clearSubmission = createAction('[Submission] Clear Submission');
